import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Text } from 'src/modules/shared/components/atoms/text';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import TextArea from 'src/modules/shared/components/atoms/inputs/text-area';
import { PiPlusLight, PiQuestionLight, PiTrash } from 'react-icons/pi';
import { AiBadge } from 'src/modules/shared/components/atoms/indicators/ai-badge';
import {
  SECTION_ORDER,
  SECTION_LABEL,
  SECTION_PLACEHOLDER,
  getSectionTooltipContent,
} from '../status-description.config';
import {
  type Sections,
  type StatusReference,
} from '../../../../../types/funnels/statuses/status-description.types';
import {
  composeDescription,
  exceptionsStringToRows,
  rowsToExceptionsString,
  extractStepNumbers,
  getSectionHeightClasses,
  parseDescription,
} from '../../../../../utils/funnels/statuses/status-description.utils';
import { type SlashCommand } from 'src/modules/shared/types/editor/slash-commands.types';
import { InlineTokensEditor } from 'src/modules/shared/components/molecules/editor/inline-tokens-editor';
import { getReferenceChipClasses } from 'src/modules/shared/components/atoms/reference-chip/styles';
import type { InlineTokenDefinition } from 'src/modules/shared/types/editor/inline-tokens.types';
import { applyEditorTextFeatures } from 'src/modules/shared/utils/editor/editor-text.util';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import type {
  EditorKey,
  EditorKeyEventFlags,
  EditorTextFeaturesConfig,
  EditorTextState,
} from 'src/modules/shared/types/editor/editor.type';
import { classes } from 'src/modules/shared/utils/styles.util';

export interface FunnelStatusDescriptionEditorProps {
  value: string;
  onChange: (nextValue: string) => void;
  disabled?: boolean;
  referenceStatuses?: StatusReference[];
  isAiLinked?: boolean;
}

export const FunnelStatusDescriptionEditor: React.FC<
  FunnelStatusDescriptionEditorProps
> = ({
  value,
  onChange,
  disabled = false,
  referenceStatuses,
  isAiLinked = false,
}) => {
  const { t } = useI18n();
  const parsed = useMemo(() => parseDescription(value || ''), [value]);
  const [sections, setSections] = useState<Sections>(parsed);
  const composedRef = useRef<string>(composeDescription(parsed));

  useEffect(() => {
    const incoming = value || '';
    if (incoming !== composedRef.current) {
      setSections(parsed);
      composedRef.current = incoming;
    }
  }, [value, parsed]);

  const handleSectionChange = (key: keyof Sections, nextValue: string) => {
    setSections((prev) => {
      const updated = { ...prev, [key]: nextValue };
      const composed = composeDescription(updated);
      composedRef.current = composed;
      onChange(composed);
      return updated;
    });
  };

  const statusCommands: SlashCommand[] = useMemo(() => {
    if (!referenceStatuses || referenceStatuses.length === 0) {
      return [];
    }

    return referenceStatuses.map((status) => ({
      id: String(status.id),
      label: status.name,
      kind: 'status',
      payload: { statusId: status.id },
    }));
  }, [referenceStatuses]);

  const validStepNumbers = useMemo(
    () => extractStepNumbers(sections.pasos),
    [sections.pasos]
  );

  const stepCommands: SlashCommand[] = useMemo(() => {
    if (!validStepNumbers.length) {
      return [];
    }

    return validStepNumbers.map((stepNumber) => ({
      id: `step-${stepNumber}`,
      label: `Paso ${stepNumber}`,
      kind: 'step',
      payload: { stepNumber },
    }));
  }, [validStepNumbers]);

  const allCommands: SlashCommand[] = useMemo(
    () => [...statusCommands, ...stepCommands],
    [statusCommands, stepCommands]
  );

  const inlineDefinitions: InlineTokenDefinition[] = useMemo(() => {
    const defs: InlineTokenDefinition[] = [];

    if (referenceStatuses && referenceStatuses.length > 0) {
      const statusById = new Map<number, string>();
      referenceStatuses.forEach((statusRef) => {
        statusById.set(statusRef.id, statusRef.name);
      });

      defs.push({
        kind: 'status',
        resolveLabel: (id: string) => {
          const numericId = Number.parseInt(id, 10);
          if (Number.isNaN(numericId)) return null;
          return statusById.get(numericId) ?? null;
        },
      });
    }

    if (validStepNumbers.length > 0) {
      const stepSet = new Set(validStepNumbers);

      defs.push({
        kind: 'step',
        resolveLabel: (id: string) => {
          const numericId = Number.parseInt(id, 10);
          if (Number.isNaN(numericId)) return null;
          if (!stepSet.has(numericId)) return null;
          return `Paso ${numericId}`;
        },
      });
    }

    return defs;
  }, [referenceStatuses, validStepNumbers]);

  const getChipClassName = (kind: string): string =>
    kind === 'step'
      ? getReferenceChipClasses('step')
      : getReferenceChipClasses('status');

  const exceptionsFeatures: EditorTextFeaturesConfig = {
    bulletLists: true,
    numberedLists: false,
    nestedNumberedLists: false,
    indentWithTab: true,
    outdentWithShiftTab: true,
    continueListOnEnter: true,
    exitListOnEmptyEnter: true,
    convertNumberedToNestedBulletOnTab: false,
    increaseBulletNestingOnTab: true,
    decreaseIndentOnShiftTab: true,
    allowPlainNewline: true,
    allowPlainTab: false,
  };

  const sectionEditorFeatures: Record<
    keyof Sections,
    EditorTextFeaturesConfig
  > = {
    entrada: {
      bulletLists: true,
      numberedLists: true,
      nestedNumberedLists: true,
      indentWithTab: true,
      outdentWithShiftTab: true,
      continueListOnEnter: true,
      exitListOnEmptyEnter: true,
      convertNumberedToNestedBulletOnTab: false,
      increaseBulletNestingOnTab: true,
      decreaseIndentOnShiftTab: true,
      allowPlainNewline: true,
      allowPlainTab: false,
    },
    objetivo: {
      bulletLists: true,
      numberedLists: true,
      nestedNumberedLists: true,
      indentWithTab: true,
      outdentWithShiftTab: true,
      continueListOnEnter: true,
      exitListOnEmptyEnter: true,
      convertNumberedToNestedBulletOnTab: false,
      increaseBulletNestingOnTab: true,
      decreaseIndentOnShiftTab: true,
      allowPlainNewline: true,
      allowPlainTab: false,
    },
    pasos: {
      bulletLists: true,
      numberedLists: true,
      nestedNumberedLists: true,
      indentWithTab: true,
      outdentWithShiftTab: true,
      continueListOnEnter: true,
      exitListOnEmptyEnter: true,
      convertNumberedToNestedBulletOnTab: true,
      increaseBulletNestingOnTab: true,
      decreaseIndentOnShiftTab: true,
      allowPlainNewline: true,
      allowPlainTab: false,
    },
    excepciones: exceptionsFeatures,
    seguimiento: {
      bulletLists: true,
      numberedLists: true,
      nestedNumberedLists: true,
      indentWithTab: true,
      outdentWithShiftTab: true,
      continueListOnEnter: true,
      exitListOnEmptyEnter: true,
      convertNumberedToNestedBulletOnTab: false,
      increaseBulletNestingOnTab: true,
      decreaseIndentOnShiftTab: true,
      allowPlainNewline: true,
      allowPlainTab: false,
    },
  };

  return (
    <div className="grid grid-cols-1 gap-3">
      {SECTION_ORDER.map((key) => {
        const label = SECTION_LABEL[key];
        const tooltipContent = getSectionTooltipContent(key, t);
        const valueForSection = sections[key];
        const heightClasses = getSectionHeightClasses(key);
        const isBaseSection = key === 'entrada' || key === 'objetivo';
        const sectionDisabled = disabled || (!isAiLinked && !isBaseSection);
        const sectionCardClasses = classes(
          'rounded-md border border-secondary-300 bg-white',
          sectionDisabled && !isBaseSection
            ? 'border-dashed border-secondary-200 bg-secondary-50/80'
            : ''
        );

        if (sectionDisabled && !isBaseSection) {
          const lockedBodyClasses = classes(
            'px-3 pb-3 pt-3 flex flex-col gap-3 overflow-hidden',
            heightClasses,
            'justify-center'
          );

          return (
            <div key={key} className={sectionCardClasses}>
              <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2 opacity-80">
                <Text size="small" weight="medium">
                  {label}
                </Text>
                <Tooltip content={tooltipContent} placement="top" size="sm">
                  <PiQuestionLight className="h-5 w-5 cursor-help" />
                </Tooltip>
              </div>
              <div className={lockedBodyClasses}>
                <div className="rounded-xl border border-secondary-200 bg-white px-4 py-4 text-center flex flex-col items-center gap-2 flex-shrink-0">
                  <AiBadge size="sm" appearance="soft" />
                  <Text
                    size="small"
                    weight="medium"
                    className="text-secondary-700"
                  >
                    {/* TODO: check when implementing i18n */}
                    Sección disponible solo para estados gestionados por IA
                  </Text>
                  <Text size="tiny" className="text-secondary-500">
                    {/* TODO: check when implementing i18n */}
                    Agrega un agente IA a este estado para habilitar esta
                    sección.
                  </Text>
                </div>
              </div>
            </div>
          );
        }

        return (
          <div key={key} className={sectionCardClasses}>
            <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2">
              <Text size="small" weight="medium">
                {label}
              </Text>
              <Tooltip content={tooltipContent} placement="top" size="sm">
                <PiQuestionLight className="h-5 w-5 cursor-help" />
              </Tooltip>
            </div>

            {key === 'excepciones' ? (
              <div className="px-3 pb-3 pt-2 flex flex-col gap-2">
                {exceptionsStringToRows(valueForSection).map((row, idx) => (
                  <div
                    className="flex items-start gap-2"
                    key={`exc-row-${idx}`}
                  >
                    <span className="text-gray-500 mt-1.5">-</span>
                    <div className="flex-1 flex items-start gap-2">
                      <TextArea
                        value={row}
                        onChange={(e) => {
                          const rows = exceptionsStringToRows(valueForSection);
                          rows[idx] = e.target.value;
                          handleSectionChange(
                            'excepciones',
                            rowsToExceptionsString(rows)
                          );
                        }}
                        onKeyDown={(event) => {
                          if (sectionDisabled) return;
                          if (event.key !== 'Enter' && event.key !== 'Tab') {
                            return;
                          }

                          const target = event.currentTarget;
                          const text = target.value;
                          const selectionStart = target.selectionStart || 0;
                          const selectionEnd =
                            target.selectionEnd || selectionStart;

                          const state: EditorTextState = {
                            text,
                            selection: {
                              start: selectionStart,
                              end: selectionEnd,
                            },
                          };

                          const flags: EditorKeyEventFlags = {
                            shiftKey: event.shiftKey,
                            altKey: event.altKey,
                            ctrlKey: event.ctrlKey,
                            metaKey: event.metaKey,
                          };

                          const result = applyEditorTextFeatures(
                            state,
                            event.key as EditorKey,
                            flags,
                            exceptionsFeatures
                          );

                          if (!result) {
                            return;
                          }

                          event.preventDefault();
                          target.value = result.text;
                          target.setSelectionRange(
                            result.selection.start,
                            result.selection.end
                          );

                          const rows = exceptionsStringToRows(valueForSection);
                          rows[idx] = result.text;
                          handleSectionChange(
                            'excepciones',
                            rowsToExceptionsString(rows)
                          );
                        }}
                        disabled={sectionDisabled}
                        rows={1}
                        size="xs"
                        resize="none"
                        autoGrow
                        className="w-full"
                      />

                      <Tooltip
                        // TODO: check when implementing i18n
                        content="Elimina la excepción"
                        placement="top"
                        size="sm"
                      >
                        <Button
                          type="button"
                          variant="danger"
                          appearance="soft"
                          size="sm"
                          isIconOnly
                          //
                          aria-label="Elimina la excepción"
                          onClick={() => {
                            const rows =
                              exceptionsStringToRows(valueForSection);
                            rows.splice(idx, 1);
                            handleSectionChange(
                              'excepciones',
                              rowsToExceptionsString(rows)
                            );
                          }}
                          disabled={sectionDisabled}
                        >
                          <PiTrash className="h-4 w-4" />
                        </Button>
                      </Tooltip>
                    </div>
                  </div>
                ))}

                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="primary"
                    appearance="filled"
                    size="xs"
                    onClick={() => {
                      const rows = exceptionsStringToRows(valueForSection);
                      rows.push('');
                      handleSectionChange(
                        'excepciones',
                        rowsToExceptionsString(rows)
                      );
                    }}
                    disabled={sectionDisabled}
                  >
                    <div className="flex items-center gap-1">
                      <PiPlusLight className="w-4 h-4" />
                      {/* TODO: check when implementing i18n */}
                      <Text size="tiny">Agregar</Text>
                    </div>
                  </Button>
                </div>
              </div>
            ) : (
              <InlineTokensEditor
                value={valueForSection}
                onChange={(next) => handleSectionChange(key, next)}
                disabled={sectionDisabled}
                placeholder={SECTION_PLACEHOLDER[key]}
                className={heightClasses}
                definitions={inlineDefinitions}
                slashCommands={allCommands}
                getChipClassName={getChipClassName}
                editorFeatures={sectionEditorFeatures[key]}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};
